import type { ReactNode } from "react";
import { atalhosPorGrupo, type Atalho } from "../ui/atalhos";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import { useT } from "../i18n";

/**
 * Tela de ajuda dos atalhos, aberta pela tecla "?".
 *
 * A lista não é escrita aqui: vem inteira de `src/ui/atalhos.ts`, o mesmo mapa
 * que o gancho usa para disparar, com as chaves do i18n que esta tela traduz.
 * Atalho novo aparece nesta tela sozinho, e atalho ainda sem tela aparece
 * marcado como em breve, em vez de sumir, como o guia de frontend pede para a
 * navegação.
 */
export function AjudaDeAtalhos({ aoFechar }: { aoFechar: () => void }): ReactNode {
  const t = useT();
  useDialogoDeAtalhos({ nome: "Atalhos de teclado", nivel: 40, aoFechar });

  const grupos = atalhosPorGrupo();

  return (
    <div
      role="dialog"
      aria-label={t("atalhos.aria")}
      onClick={aoFechar}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
        zIndex: 40,
      }}
    >
      <div
        className="card"
        style={{ width: "min(740px, 100%)", maxHeight: "88vh", overflow: "auto", padding: 22 }}
        onClick={(evento) => evento.stopPropagation()}
      >
        <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
          <div>
            <span className="label">{t("atalhos.rotulo")}</span>
            <h2 style={{ fontSize: 20, marginTop: 4 }}>{t("atalhos.titulo")}</h2>
          </div>
          <span className="label-muted">{t("atalhos.fecheComEsc")}</span>
        </div>

        <p className="muted" style={{ margin: "10px 0 18px", fontSize: 12 }}>
          {t("atalhos.explicacao")}
        </p>

        <div className="ajuda-grade">
          {grupos.map((grupo) => (
            <section key={`${grupo.grupo}-${grupo.itens.length}`}>
              <h3 className="label" style={{ marginBottom: 6 }}>
                {t(grupo.grupo)}
              </h3>
              {grupo.itens.map((atalho, indice) => (
                <Linha key={`${atalho.descricao}-${indice}`} atalho={atalho} />
              ))}
            </section>
          ))}
        </div>

        <button className="primary" style={{ width: "100%", marginTop: 22 }} onClick={aoFechar}>
          {t("atalhos.voltarAoJogo")}
        </button>
      </div>
    </div>
  );
}

function Linha({ atalho }: { atalho: Atalho }): ReactNode {
  const t = useT();
  return (
    <div className="ajuda-linha">
      <span className="ajuda-teclas">
        {atalho.teclas.map((tecla, indice) => (
          <span key={tecla}>
            {indice > 0 && atalho.ligacao && (
              <span className="ajuda-ligacao" style={{ marginRight: 4 }}>
                {t(atalho.ligacao)}
              </span>
            )}
            <kbd className={`tecla${atalho.emBreve ? " tecla-breve" : ""}`}>{tecla}</kbd>
          </span>
        ))}
      </span>
      <span
        className="muted"
        style={{ fontSize: 12, opacity: atalho.emBreve ? 0.55 : 1, minWidth: 0 }}
      >
        {t(atalho.descricao)}
        {atalho.emBreve && (
          <span className="label-muted" style={{ marginLeft: 6 }}>
            {t("atalhos.emBreve")}
          </span>
        )}
      </span>
    </div>
  );
}
